import React, { useEffect, useState } from "react";
import "../assets/styles/ProductReviews.css";
import { ReviewBaseUrl } from "../config/api";
import reviewStarsIcon from "../assets/images/review-stars-icon.png";

const ProductReviews = ({ productId, productName }) => {
  const [activeTab, setActiveTab] = useState("reviews");
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  const [loading, setLoading] = useState(true);

  const [newReview, setNewReview] = useState({
    userName: "",
    userEmail: "",
    rating: 0,
    title: "",
    content: "",
    wouldRecommend: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const buildCandidateUrls = (path) => {
    const base = (ReviewBaseUrl || "").replace(/\/$/, "");
    const cleanPath = (path || "").replace(/^\//, "");
    return [`${base}/${cleanPath}`, `${base}/api/${cleanPath}`];
  };

  const readResponseBody = async (response) => {
    const contentType = response.headers?.get?.("content-type") || "";
    if (contentType.includes("application/json")) {
      return response.json();
    }
    const text = await response.text();
    return { _nonJson: true, text };
  };

  const fetchFirstOk = async (path, options) => {
    let lastError;
    for (const url of buildCandidateUrls(path)) {
      try {
        const requestOptions = options
          ? { cache: "no-store", ...options }
          : { cache: "no-store" };
        const response = await fetch(url, requestOptions);
        if (!response.ok) {
          const body = await readResponseBody(response);
          lastError = new Error(
            body && body._nonJson
              ? `Request failed (${response.status})`
              : body?.error || body?.message || `Request failed (${response.status})`
          );
          continue;
        }
        const body = await readResponseBody(response);
        if (body && body._nonJson) {
          lastError = new Error("Unexpected non-JSON response");
          continue;
        }
        return body;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError || new Error("Request failed");
  };

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const data = await fetchFirstOk(`reviews/product/${productId}`);
      setReviews(data.reviews || []);
      setTotalReviews(data.totalReviews || 0);
      setAverageRating(data.averageRating || 0);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setReviews([]);
      setTotalReviews(0);
      setAverageRating(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      fetchReviews();
    }
  }, [productId]);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <span key={index} className={`star ${index < rating ? "filled" : ""}`}>
        ★
      </span>
    ));
  };

  const handleStarClick = (rating) => {
    setNewReview((prev) => ({ ...prev, rating }));
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (!newReview.userName || !newReview.userEmail || !newReview.rating || !newReview.title || !newReview.content) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      setSubmitting(true);
      await fetchFirstOk(`reviews/product/${productId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newReview),
      });

      setNewReview({
        userName: "",
        userEmail: "",
        rating: 0,
        title: "",
        content: "",
        wouldRecommend: false,
      });

      setShowWriteReview(false);
      setActiveTab("reviews");
      alert("Review submitted successfully. It will be visible after admin approval.");
      fetchReviews();
    } catch (error) {
      console.error("Error submitting review:", error);
      alert(error.message || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="product-reviews-section">
      <div className="reviews-tabs">
        <button
          type="button"
          className={`reviews-tab ${activeTab === "reviews" ? "active" : ""}`}
          onClick={() => setActiveTab("reviews")}
        >
          Product Reviews
        </button>
        <button
          type="button"
          className={`reviews-tab ${activeTab === "write" ? "active" : ""}`}
          onClick={() => setActiveTab("write")}
        >
          Write to Us
        </button>
      </div>

      {loading && (
        <div className="loading-state">
          <p>Loading reviews...</p>
        </div>
      )}

      {!loading && activeTab === "reviews" && reviews.length > 0 && (
        <>
          <div className="reviews-summary-mini">
            <span className="summary-rating">{Number(averageRating || 0).toFixed(1)}</span>
            <div className="summary-stars">{renderStars(Math.round(averageRating || 0))}</div>
            <span className="summary-count">{totalReviews} reviews for {productName}</span>
          </div>

          <div className="reviews-list">
            {reviews.map((review) => (
              <div key={review._id || review.id} className="review-card">
                <div className="review-header">
                  <div className="review-stars">{renderStars(review.rating)}</div>
                  <span className="review-date">{review.formattedDate || review.date}</span>
                </div>
                <h4 className="review-title">{review.title}</h4>
                <p className="review-text">{review.content}</p>
                <p className="review-user">By {review.userName}</p>
              </div>
            ))}
          </div>
        </>
      )}

      {!loading && (activeTab === "write" || reviews.length === 0) && (
        <div className="reviews-empty-state">
          <div className="reviews-empty-icon" aria-hidden="true">
            <img src={reviewStarsIcon} alt="" />
          </div>
          <h3>We love the stars</h3>
          <p>Let us know what you think</p>
          <button
            type="button"
            className="reviews-primary-cta"
            onClick={() => setShowWriteReview(true)}
          >
            Be the first to review this product
          </button>
        </div>
      )}

      {showWriteReview && (
        <div className="review-modal-overlay" onClick={() => setShowWriteReview(false)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowWriteReview(false)}>×</button>
            <h3>Write a Review</h3>
            <form className="review-form" onSubmit={handleSubmitReview}>
              <div className="form-group">
                <label>Your Name</label>
                <input
                  type="text"
                  placeholder="Enter your name"
                  value={newReview.userName}
                  onChange={(e) => setNewReview((prev) => ({ ...prev, userName: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Your Email</label>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={newReview.userEmail}
                  onChange={(e) => setNewReview((prev) => ({ ...prev, userEmail: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Your Rating</label>
                <div className="star-input">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`star-select ${star <= newReview.rating ? "selected" : ""}`}
                      onClick={() => handleStarClick(star)}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>Review Title</label>
                <input
                  type="text"
                  placeholder="Give your review a title"
                  value={newReview.title}
                  onChange={(e) => setNewReview((prev) => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Your Review</label>
                <textarea
                  placeholder="Write your review here..."
                  rows="4"
                  value={newReview.content}
                  onChange={(e) => setNewReview((prev) => ({ ...prev, content: e.target.value }))}
                  required
                ></textarea>
              </div>
              <div className="form-group checkbox">
                <input
                  type="checkbox"
                  id="recommend"
                  checked={newReview.wouldRecommend}
                  onChange={(e) => setNewReview((prev) => ({ ...prev, wouldRecommend: e.target.checked }))}
                />
                <label htmlFor="recommend">I would recommend this product</label>
              </div>
              <button type="submit" className="submit-review-btn" disabled={submitting}>
                {submitting ? "Submitting..." : "Submit Review"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
